<template>
  <div class="container mt-4">
    <!-- Loading State -->
    <div v-if="loading">
      <div class="row">
        <div class="col-md-6 mx-auto">
          <div class="card shadow-sm">
            <div class="card-body text-center py-5">
              <!-- Spinner -->
              <div class="mb-4">
                <i class="fas fa-circle-notch fa-spin fa-3x text-primary"></i>
              </div>
              <h4 class="mb-3">Checking Payment Status</h4>
              <p class="text-muted mb-0">Please wait while we confirm your payment...</p>

              <!-- Progressive warning messages -->
              <div class="alert alert-warning mt-4 mb-0" v-if="retries >= 2 && retries < 20">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                <span v-if="retries >= 2 && retries < 4">
                  This is taking longer than usual. Please keep this window open.
                </span>
                <span v-else-if="retries >= 4 && retries <= 6">
                  Waiting for payment confirmation. Do not close or refresh this page.
                </span>
                <span v-else-if="retries > 6">
                  Still processing. The system is working on your request. Please do not close or refresh this page.
                </span>
              </div>

              <div class="alert alert-danger mt-4 mb-0" v-if="retries >= 20 || unpaidRetries >= 20">
                <i class="fas fa-times-circle mr-2"></i>
                The system may have encountered a problem. Please contact
                <a href="mailto:support@eventrac.co.uk">support@eventrac.co.uk</a>
                and quote reference: <strong>{{ invoiceIds.join(', ') }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success State (all paid) -->
    <div v-else-if="!hasUnpaidItems">
      <div class="row">
        <div class="col-md-12 text-center mb-4">
          <p class="h1 mb-0 mt-4 text-success">
            <b-icon-check-circle></b-icon-check-circle>
          </p>
          <h3 class="text-success mt-2">Congratulations</h3>
          <p>
            Your bookings are now complete. We are dispatching the confirmation emails to you,
            so keep an eye on your inbox.
          </p>
        </div>
      </div>

      <!-- Checkout Complete Notes -->
      <div class="row" v-if="checkoutCompleteNotes.length > 0">
        <div class="col-md-12 text-center mb-4">
          <div v-for="(note, index) in checkoutCompleteNotes" :key="index" v-html="note"></div>
        </div>
      </div>

      <!-- Paid Items List -->
      <div class="row" v-if="paidItems.length > 0">
        <div class="col-md-12">
          <div class="card card-success mb-3">
            <div class="card-header">
              Successful Transactions
            </div>
            <div class="card-body p-0">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>Item Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in paidItems" :key="item.id">
                    <td>{{ item.name }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- View Bookings Button -->
      <div class="row">
        <div class="col-md-12 text-center">
          <a :href="profileUrl" class="btn btn-success mb-4">View My Bookings</a>
        </div>
      </div>
    </div>

    <!-- Failure State (some unpaid) -->
    <div v-else>
      <div class="row">
        <div class="col-md-12 text-center mb-4">
          <h3 class="text-danger mt-4">Unsuccessful</h3>
          <p>
            Unfortunately {{ unpaidItems.length }} of your items were unsuccessful. Please try again.
          </p>
        </div>
      </div>

      <!-- Unpaid Items List -->
      <div class="row" v-if="unpaidItems.length > 0">
        <div class="col-md-12">
          <div class="card card-danger mb-3">
            <div class="card-header">
              Unsuccessful Transactions
            </div>
            <div class="card-body p-0">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Failure Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in unpaidItems" :key="item.id">
                    <td>{{ item.name }}</td>
                    <td>
                      <ul class="mb-0 pl-3">
                        <li v-for="(log, index) in item.payment_logs" :key="index">
                          {{ log.payment_provider_message }}
                        </li>
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Try Again Button -->
      <div class="row">
        <div class="col-md-12 text-center">
          <a :href="checkoutUrl" class="btn btn-danger">Try Again</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { BIconCheckCircle } from 'bootstrap-vue';

export default {
  name: 'checkout-complete',
  components: {
    BIconCheckCircle,
  },
  props: {
    invoiceIds: {
      type: Array,
      required: true,
    },
    profileUrl: {
      type: String,
      required: true,
    },
    checkoutUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      invoiceItems: null,
      loading: true,
      retries: 0,
      unpaidRetries: 0,
      retryTimeout: null,
      isRedirecting: false,
    };
  },
  computed: {
    /**
     * Get paid invoice items
     */
    paidItems() {
      if (!this.invoiceItems || !this.invoiceItems.paid) {
        return [];
      }
      return this.invoiceItems.paid;
    },
    /**
     * Get unpaid invoice items
     */
    unpaidItems() {
      if (!this.invoiceItems || !this.invoiceItems.unpaid) {
        return [];
      }
      return this.invoiceItems.unpaid;
    },
    /**
     * Check if there are any unpaid items
     */
    hasUnpaidItems() {
      return this.unpaidItems.length > 0;
    },
    /**
     * Get unique checkout complete notes from paid items
     */
    checkoutCompleteNotes() {
      if (!this.paidItems.length) {
        return [];
      }
      const notes = this.paidItems
        .filter(item =>
          item.participant &&
          item.participant.registration_category &&
          item.participant.registration_category.checkout_complete_note
        )
        .map(item => item.participant.registration_category.checkout_complete_note);

      // Return unique notes
      return [...new Set(notes)];
    },
    /**
     * Build the retrieve API URL
     */
    retrieveUrl() {
      const params = this.invoiceIds.map(id => `invoices[]=${id}`).join('&');
      return `/api/v0/invoice_items/retrieve?${params}`;
    },
    /**
     * Find a participant with fundraising requested (JustGiving or EventGiving)
     */
    fundraisingParticipant() {
      if (!this.paidItems.length) {
        return null;
      }
      const item = this.paidItems.find(item =>
        item.participant &&
        (item.participant.just_giving_requested || item.participant.eventrac_giving_requested)
      );
      return item ? item.participant : null;
    },
    /**
     * Build the fundraising page setup URL if a participant requested it
     */
    fundraisingSetupUrl() {
      if (!this.fundraisingParticipant) {
        return null;
      }
      return `/fundraising/create/${this.fundraisingParticipant.id}`;
    },
  },
  mounted() {
    // Add warning when user tries to leave during payment check
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // Use nextTick to ensure loading state renders before API call
    this.$nextTick(() => {
      this.retrieveInvoiceItems();
    });
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    clearTimeout(this.retryTimeout);
  },
  methods: {
    /**
     * Handle beforeunload event - warn user if still loading
     */
    handleBeforeUnload(event) {
      if (this.loading && !this.isRedirecting) {
        const message = 'Please do not exit this page while your payment is being processed. Leaving now may result in payment issues.';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    },
    /**
     * Retrieve invoice items via API polling
     */
    async retrieveInvoiceItems() {
      try {
        const response = await axios.get(this.retrieveUrl, {
          timeout: 10000,
        });

        if (response.data.code === 500 || response.data.data == null) {
          throw new Error('Failed to retrieve invoice items');
        }

        this.invoiceItems = response.data.data;

        // If items are still unpaid, Stripe may not have finalised the payment intent yet.
        // Retry rather than immediately showing the failure state (race condition on redirect).
        // Uses a separate counter so network-error retries don't consume the Stripe grace window.
        if (this.hasUnpaidItems && this.unpaidRetries < 20) {
          this.unpaidRetries++;
          this.retryTimeout = setTimeout(() => this.retrieveInvoiceItems(), 5000);
          return;
        }

        // Retries exhausted and items still unpaid — stay in loading state so the
        // "contact support" banner (v-if="retries >= 20") is visible rather than
        // showing "Try Again" which risks a double-charge attempt.
        if (this.hasUnpaidItems) {
          return;
        }

        if (!this.paidItems.length) {
          this.loading = false;
          return;
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'page_view',
          pageTitle: 'Checkout Complete',
        });

        try {
          const transactionId = this.buildTransactionId(this.paidItems);
          window.dataLayer.push({
            event: 'purchase',
            transaction_id: transactionId,
            value: this.paidItems.reduce((acc, item) => acc + (item.total_amount / 100), 0),
            currency: this.paidItems[0].currency || 'GBP',
            items: this.buildGtmItems(this.paidItems),
          });
        } catch (gtmError) {
          console.error('GTM purchase event failed', gtmError);
        }

        // Redirect to fundraising setup if requested (don't show success page)
        if (this.fundraisingSetupUrl) {
          this.isRedirecting = true;
          window.location.href = this.fundraisingSetupUrl;
          return;
        }

        this.loading = false;
        this.triggerConfetti();
      } catch (error) {
        this.retries++;

        // Retry up to 20 times with 5 second intervals
        if (this.retries <= 20) {
          this.retryTimeout = setTimeout(() => this.retrieveInvoiceItems(), 5000);
        }
        // When retries exhaust, loading stays true so the contact-support banner shows.
      }
    },
    buildTransactionId(items) {
      return items.map((item) => `INV_${item.id}`).join('_');
    },
    buildGtmItems(items) {
      const typeMap = [
        { idKey: 'participant_id', category: 'participant', getId: (item) => item.participant && item.participant.registration_category_id !== null ? item.participant.registration_category_id.toString() : item.id.toString() },
        { idKey: 'team_id', category: 'team', getId: (item) => item.team && item.team.registration_category_id !== null ? item.team.registration_category_id.toString() : item.id.toString() },
        { idKey: 'donation_id', category: 'donation', getId: (item) => item.donation && item.donation.charity_id !== null ? item.donation.charity_id.toString() : item.id.toString() },
        { idKey: 'credit_id', category: 'credit', getId: null },
        { idKey: 'order_id', category: 'order', getId: (item) => item.order && item.order.sku_id !== null ? item.order.sku_id.toString() : item.id.toString(), getQuantity: (item) => item.order.quantity },
        { idKey: 'policy_holder_id', category: 'policy_holder', getId: (item) => item.policy_holder && item.policy_holder.insurance_policy_id !== null ? item.policy_holder.insurance_policy_id.toString() : item.id.toString() },
      ];

      return items.map((item) => {
        const type = typeMap.find(({ idKey }) => item[idKey] !== null);
        return {
          item_id: type && type.getId ? type.getId(item) : item.id.toString(),
          item_name: item.name,
          item_category: type ? type.category : '',
          quantity: type && type.getQuantity ? type.getQuantity(item) : 1,
          price: item.total_amount / 100,
        };
      });
    },
    /**
     * Trigger confetti animation
     */
    triggerConfetti() {
      // Wait a moment then trigger confetti (ensure script is loaded)
      setTimeout(() => {
        if (window.confetti && typeof window.confetti.start === 'function') {
          window.confetti.start();
          // Stop after 4 seconds
          setTimeout(() => {
            if (window.confetti && typeof window.confetti.stop === 'function') {
              window.confetti.stop();
            }
          }, 4000);
        }
      }, 100);
    },
  },
};
</script>

<style scoped>
.card-success .card-header {
  background-color: #28a745;
  color: white;
}
.card-danger .card-header {
  background-color: #dc3545;
  color: white;
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Checkout/CheckoutComplete.vue